import { z } from "zod"

import type { AppComms, Audience, Incentive, IncentiveFields } from "@/hooks/useIncentives"

import {
  APP_COMMS_MAX_LENGTHS,
  INCENTIVE_PERIODS,
  INCENTIVE_UNITS,
  ROUTE_CYCLES,
  ROUTE_TYPES,
} from "../../../shared/incentives"
import { ROUTE_TYPE_ADJECTIVES } from "./routeTypeLabels"

const POUNDS = /^\d+(\.\d{1,2})?$/

const WHOLE_NUMBER = /^\d+$/

const AudienceSchema = z.object({
  areas: z.array(z.string()),
  types: z.array(z.string()),
  roles: z.array(z.string()),
  depots: z.array(z.string()),
})

const atMost = (characters: number) => `At most ${characters} characters`

// Terms stay a string while being typed. Cleared title and text mean no App Comms at all.
const AppCommsSchema = z.object({
  title: z.string().trim().max(APP_COMMS_MAX_LENGTHS.title, atMost(APP_COMMS_MAX_LENGTHS.title)),
  text: z.string().trim().max(APP_COMMS_MAX_LENGTHS.text, atMost(APP_COMMS_MAX_LENGTHS.text)),
  terms: z.string().trim().max(APP_COMMS_MAX_LENGTHS.terms, atMost(APP_COMMS_MAX_LENGTHS.terms)),
  show_homepage_banner: z.boolean(),
})

// Amount and threshold stay strings while being typed, so a cleared input isn't read as 0.
export const incentiveFormSchema = z
  .object({
    name: z.string().trim().min(1, "Name is required"),
    start_date: z.string().min(1, "Start date is required"),
    end_date: z.string(),
    pay_description: z.string().trim().min(1, "Pay description is required"),
    route_types: z.array(z.enum(ROUTE_TYPES)).min(1, "Tick at least one route type"),
    route_cycles: z.array(z.enum(ROUTE_CYCLES)).min(1, "Tick at least one route cycle"),
    // null until Ops choose, so neither answer is picked for them
    one_route_per_day: z
      .boolean()
      .nullable()
      .refine((value) => value !== null, "Choose whether every route counts"),
    amount: z
      .string()
      .refine(
        (amount) => POUNDS.test(amount) && Number(amount) > 0,
        "Enter an amount in pounds, more than £0",
      ),
    unit: z.enum(INCENTIVE_UNITS),
    threshold: z.string(),
    period: z.enum(INCENTIVE_PERIODS),
    audiences: z.array(AudienceSchema).min(1, "Add at least one audience"),
    app_comms: AppCommsSchema,
  })
  .superRefine((values, ctx) => {
    const atThreshold = values.unit === "threshold"
    if (atThreshold && !(WHOLE_NUMBER.test(values.threshold) && Number(values.threshold) > 0)) {
      ctx.addIssue({
        code: "custom",
        path: ["threshold"],
        message: `Enter a whole number of ${values.one_route_per_day ? "days" : "routes"}, more than 0`,
      })
    }
    if (atThreshold && values.period === "whole_run" && !values.end_date) {
      ctx.addIssue({
        code: "custom",
        path: ["end_date"],
        message: "A whole incentive needs an end date",
      })
    } else if (values.end_date && values.start_date && values.end_date < values.start_date) {
      ctx.addIssue({
        code: "custom",
        path: ["end_date"],
        message: "End date must be on or after the start date",
      })
    }
    const { title, text } = values.app_comms
    if (title.trim() && !text.trim()) {
      ctx.addIssue({ code: "custom", path: ["app_comms", "text"], message: "Add the text too" })
    }
    if (text.trim() && !title.trim()) {
      ctx.addIssue({ code: "custom", path: ["app_comms", "title"], message: "Add a title too" })
    }
    values.audiences.forEach((audience, index) => {
      if (Object.values(audience).every((options) => options.length === 0)) {
        ctx.addIssue({
          code: "custom",
          path: ["audiences", index],
          message: "Choose at least one area, type, role or depot",
        })
      }
    })
  })

export type IncentiveFormValues = z.infer<typeof incentiveFormSchema>

type AppCommsValues = IncentiveFormValues["app_comms"]

export const hasAppComms = ({ title, text }: AppCommsValues) => !!(title.trim() && text.trim())

export const emptyAudience = (): Audience => ({ areas: [], types: [], roles: [], depots: [] })

export const NEW_INCENTIVE: IncentiveFormValues = {
  name: "",
  start_date: "",
  end_date: "",
  pay_description: "",
  route_types: ["completed"],
  route_cycles: [],
  one_route_per_day: null,
  amount: "",
  unit: "per_route",
  threshold: "",
  period: "weekly",
  audiences: [emptyAudience()],
  app_comms: { title: "", text: "", terms: "", show_homepage_banner: false },
}

export const valuesFrom = (incentive: Incentive): IncentiveFormValues => ({
  name: incentive.name,
  start_date: incentive.start_date,
  end_date: incentive.end_date ?? "",
  pay_description: incentive.pay_description,
  route_types: incentive.route_types,
  route_cycles: incentive.route_cycles,
  one_route_per_day: incentive.one_route_per_day,
  amount: String(incentive.amount),
  unit: incentive.unit,
  threshold: incentive.threshold == null ? "" : String(incentive.threshold),
  period: incentive.period,
  audiences: incentive.audiences,
  app_comms: {
    title: incentive.app_comms?.title ?? "",
    text: incentive.app_comms?.text ?? "",
    terms: incentive.app_comms?.terms ?? "",
    show_homepage_banner: incentive.app_comms?.show_homepage_banner ?? false,
  },
})

const appCommsFrom = (values: AppCommsValues): AppComms | null =>
  hasAppComms(values)
    ? {
        title: values.title.trim(),
        text: values.text.trim(),
        terms: values.terms.trim() || null,
        show_homepage_banner: values.show_homepage_banner,
      }
    : null

// A per-route incentive pays every route in the week it's driven, so it's always weekly.
export const bodyFrom = (values: IncentiveFormValues): IncentiveFields => {
  const atThreshold = values.unit === "threshold"
  return {
    name: values.name,
    start_date: values.start_date,
    end_date: values.end_date || null,
    pay_description: values.pay_description,
    route_types: values.route_types,
    route_cycles: values.route_cycles,
    one_route_per_day: values.one_route_per_day,
    amount: Number(values.amount),
    unit: values.unit,
    threshold: atThreshold ? Number(values.threshold) : null,
    period: atThreshold ? values.period : "weekly",
    audiences: values.audiences,
    app_comms: appCommsFrom(values.app_comms),
  }
}

export type FieldError = {
  name: `audiences.${number}` | `app_comms.${keyof AppCommsValues}` | keyof IncentiveFormValues
  message: string
}

const FIELDS = new Set(Object.keys(NEW_INCENTIVE))

const APP_COMMS_FIELDS = new Set(Object.keys(NEW_INCENTIVE.app_comms))

// The API joins its validation errors as "field: message; audiences.0: message". Those naming a form
// field go against it; the rest come back unmatched for the page to show on their own.
export const fieldErrorsFrom = (message: string) => {
  const fieldErrors: FieldError[] = []
  const unmatched: string[] = []
  for (const part of message.split("; ")) {
    const [, path, text] = part.match(/^([\w.]+): (.+)$/) ?? []
    const [field, key] = path?.split(".") ?? []
    if (!FIELDS.has(field)) {
      unmatched.push(part)
    } else if (field === "audiences" && /^\d+$/.test(key ?? "")) {
      fieldErrors.push({ name: `audiences.${Number(key)}`, message: text })
    } else if (field === "app_comms" && APP_COMMS_FIELDS.has(key)) {
      fieldErrors.push({ name: `app_comms.${key as keyof AppCommsValues}`, message: text })
    } else {
      fieldErrors.push({ name: field as keyof IncentiveFormValues, message: text })
    }
  }
  return { fieldErrors, unmatched }
}

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]

// YYYY-MM-DD dates are calendar days with no time of day, so reading the weekday in UTC is safe.
const weekdayOf = (date: string) => new Date(`${date}T00:00:00Z`).getUTCDay()

export const partialWeekWarning = (values: IncentiveFormValues): string | null => {
  if (values.unit !== "threshold" || values.period !== "weekly" || !values.start_date) return null

  const startsMonday = weekdayOf(values.start_date) === 1
  const endsSunday = !values.end_date || weekdayOf(values.end_date) === 0
  if (startsMonday && endsSunday) return null

  const off = [
    !startsMonday && `starts on a ${WEEKDAYS[weekdayOf(values.start_date)]}`,
    !endsSunday && `ends on a ${WEEKDAYS[weekdayOf(values.end_date)]}`,
  ].filter(Boolean)
  return (
    `Every week counts each Monday to Sunday on its own. This incentive ${off.join(" and ")}, ` +
    "so a part week is counted on its own days and drivers may not reach the threshold in it. " +
    "If the offer is for its whole run, choose Whole incentive instead."
  )
}

// A part of the rule summary read from a field, which the form shows in bold.
export type SummaryPart = string | { value: string }

const MISSING = "…"

const eitherOf = (words: string[]) =>
  words.length < 2 ? (words[0] ?? MISSING) : `${words.slice(0, -1).join(", ")} or ${words.at(-1)}`

// In the constants' order rather than the order they were ticked, so the sentence doesn't shuffle.
const inOrder = <T>(all: readonly T[], picked: T[]) => all.filter((value) => picked.includes(value))

// The rule read back as one sentence, so Ops can check it against the offer they were given.
export const ruleSummary = (values: IncentiveFormValues): SummaryPart[] => {
  const amount = { value: values.amount ? `£${values.amount}` : MISSING }
  const types = eitherOf(
    inOrder(ROUTE_TYPES, values.route_types).map((type) => ROUTE_TYPE_ADJECTIVES[type]),
  )
  const routes = { value: `${types} ${eitherOf(inOrder(ROUTE_CYCLES, values.route_cycles))}` }
  const perDay = values.one_route_per_day === true

  if (values.unit === "per_route") {
    const article = /^[aeiou]/.test(types) ? "an" : "a"
    return perDay
      ? ["Pays ", amount, ` for each day with ${article} `, routes, " route."]
      : ["Pays ", amount, " for each ", routes, " route."]
  }

  const weekly = values.period === "weekly"
  const threshold = values.threshold || MISSING
  return [
    "Pays ",
    amount,
    weekly ? " each week (Mon-Sun) a driver has " : " once a driver has ",
    { value: threshold },
    " ",
    routes,
    threshold === "1" ? " route" : " routes",
    ...(perDay ? [", ", { value: "at most one a day" }] : []),
    ...(weekly ? ["."] : [", across the ", { value: "whole incentive" }, "."]),
  ]
}
